import { Common } from './common';
import { User } from './models/user';
import {
  CreateValues,
  FindAllOptions,
  Practice,
  PracticeFull,
  ProviderPractice,
  PracticeBulkCreateValues,
  PracticeAccount,
  UpdateValues,
} from './models/practice';
import { Organization } from './models/organization';
import { PracticeGroup } from './models/practiceGroup';
import { ImportValidation } from './models/provider';
import { EhrProviderInformationPopulated } from './models/ehrProviderInformation';

export class API {
  common: Common;

  constructor(common: Common) {
    this.common = common;
  }

  async create(values: CreateValues): Promise<Practice> {
    const url = this.common.constructPath('/practice');
    return this.common.postJSON(url, values);
  }

  async createWithPreferences(
    practices: PracticeBulkCreateValues[],
  ): Promise<Practice> {
    const url = this.common.constructPath('/practice');
    return this.common.postJSON(url, practices);
  }

  async findAll(query: FindAllOptions = {}): Promise<Practice[]> {
    const url = this.common.constructPath('/practice', query);
    const { practices } = await this.common.getJSON(url);
    return practices;
  }

  async findOneById(id: string): Promise<PracticeFull> {
    const url = this.common.constructPath(`/practice/${id}`);
    const { practice } = await this.common.getJSON(url);
    return practice;
  }

  async findAllProvidersByPracticeId(id: string): Promise<User[]> {
    const url = this.common.constructPath(`/practice/${id}/providers`);
    const { providers } = await this.common.getJSON(url);
    return providers;
  }

  async addProvider(
    practiceId: string,
    providerUserId: string,
  ): Promise<ProviderPractice> {
    const url = this.common.constructPath(`/practice/${practiceId}/provider`);
    const { added } = await this.common.postJSON(url, { providerUserId });
    return added;
  }

  async addAccount(
    practiceId: string,
    accountId: string,
  ): Promise<PracticeAccount> {
    const url = this.common.constructPath(`/practice/${practiceId}/account/`);
    return this.common.postJSON(url, { accountId });
  }

  async updateAccount(
    practiceId: string,
    accountId: string,
  ): Promise<PracticeAccount> {
    const url = this.common.constructPath(`/practice/${practiceId}/account/`);
    return this.common.patchJSON(url, { accountId });
  }

  async deleteAccountByPracticeId(
    practiceId: string,
  ): Promise<{ success: boolean }> {
    const url = this.common.constructPath(`/practice/${practiceId}/account/`);
    return this.common.deleteJSON(url);
  }

  async findAccountByPracticeId(practiceId: string): Promise<PracticeAccount> {
    const url = this.common.constructPath(`/practice/${practiceId}/account/`);
    return this.common.getJSON(url);
  }

  async syncCalendar(
    practiceId: string,
    formattedDate?: string,
  ): Promise<boolean> {
    // formattedDate in YYYY-MM-DD format
    const url = this.common.constructPath(`/calendar/sync`);
    const body = {
      date: formattedDate,
      practiceId: practiceId,
    };
    const { success } = await this.common.postJSON(url, body);
    return success;
  }

  async update(id: string, values: UpdateValues): Promise<Practice> {
    const url = this.common.constructPath(`/practice/${id}`);
    const { practice } = await this.common.patchJSON(url, values);
    return practice;
  }

  async findAllByProviderId(providerId: string): Promise<{
    user: User;
    practices: (Practice & {
      ehrProvider?: EhrProviderInformationPopulated;
    })[];
    practiceGroups: PracticeGroup[];
    organizations: Organization[];
  }> {
    const url = this.common.constructPath(`/provider/${providerId}/practices`);
    return this.common.getJSON(url);
  }

  async verifyImport(
    values: Record<string, string>[],
  ): Promise<Record<string, ImportValidation>[]> {
    const url = this.common.constructPath(`/practice/import/verify`);
    return this.common.postJSON(url, values);
  }
}
